import { resolveCrmTicketDetailPortalView } from './crmCommunicationPermissions';
import { CRM_COMM_MASK_DETAIL, CRM_COMM_MASK_PORTAL_DETAIL } from '../pages/crm/CrmCommunicationTicketsList';

import { parseEntityShowResponse } from './entityShowResponse';
import { canCreateChecklistRecord } from './checklistRecordCreateHttp';

export function toDeepLinkInt(value) {
  const n = parseInt(String(value ?? ''), 10);
  return Number.isFinite(n) ? n : null;
}

/** Odpoveď GET /api/{resource}/{id} – null pri 404 alebo chýbajúcom id. */
export { parseEntityShowResponse } from './entityShowResponse';

function openCompanyCertDeepLink(ctx, { page, propKey, mask, titleKey, companyId }) {
  const { dispatch, t, getRequest, withDetailLoader, showOpenTabWarn } = ctx;
  const id = toDeepLinkInt(companyId);
  if (id == null) return;
  withDetailLoader(() =>
    getRequest(`/api/companies/${id}`).then((res) => {
      const company = parseEntityShowResponse(res);
      if (company?.id) {
        dispatch({
          type: 'create_new_tab',
          name: t(titleKey), translationKey: titleKey, legacyLabel: t(titleKey),
          page,
          props: { [propKey]: company },
          mask,
        });
      } else {
        showOpenTabWarn();
      }
    }).catch(() => {
      showOpenTabWarn();
    }),
  );
}

function openPriorityListDeepLink(ctx, query) {
  const { dispatch, t, getRequest } = ctx;
  const priorityId = query.get('priority_id');
  const problemId = query.get('problem_id') || null;
  const query_props = {
    query_priority_id: priorityId,
    query_priority_problem_id: problemId,
  };
  const openTab = () => {
    dispatch({
      type: 'create_new_tab', name: t('Util.Tab.PriorityList'), translationKey: 'Util.Tab.PriorityList', legacyLabel: t('Util.Tab.PriorityList'),
      page: 'PriorityList',
      props: { query_props },
      mask: 'prio001',
    });
  };
  if (!priorityId) {
    openTab();
    return;
  }
  getRequest(`/api/priorities/${priorityId}`).then((res) => {
    const priority = parseEntityShowResponse(res);
    if (priority?.id) {
      dispatch({ type: 'upsert_priorities', priorities: [priority] });
    }
    openTab();
  }).catch(openTab);
}

/**
 * Spracuje ?tab=... deep linky z URL (email, copy link, openTab).
 * Entity detaily načítava cez API – nezávisí od progressive / vynechaného startRequest state.
 */
export function processDeepLinksFromSearch(search, ctx) {
  if (!search) return;
  const { user, dispatch, t, getRequest, withDetailLoader, showOpenTabWarn, openErrorRecordFromApi } = ctx;
  const query = new URLSearchParams(search);
  const tab = query.get('tab');
  if (!tab) return;

  if (tab === 'PriorityList') {
    openPriorityListDeepLink(ctx, query);
    return;
  }

  if (tab === 'InternaChyba' || tab === 'ExternaChyba' || tab === 'Reklamacia') {
    openErrorRecordFromApi?.(query.get('error_record_id'), tab);
    return;
  }

  if (tab === 'PaintingConditionAdd') {
    const paintingConditionId = query.get('painting_condition_id');
    if (paintingConditionId) {
      withDetailLoader(() =>
        getRequest(`/api/painting_conditions/${paintingConditionId}`).then((res) => {
          const condition = parseEntityShowResponse(res);
          if (condition?.id) {
            dispatch({
              type: 'create_new_tab', name: t('Util.Tab.EditPaintingConditions'), translationKey: 'Util.Tab.EditPaintingConditions', legacyLabel: t('Util.Tab.EditPaintingConditions'),
              page: 'PaintingConditionAdd',
              props: { painting_condition: condition, type: 'edit' },
              mask: 'pain003',
            });
          } else {
            showOpenTabWarn(t('Util.DeepLink.Warn.PaintingConditions'));
          }
        }).catch(() => {
          showOpenTabWarn(t('Util.DeepLink.Warn.PaintingConditions'));
        }),
      );
    }
    return;
  }

  if (tab === 'GaugeCalibrationAdd') {
    const id = toDeepLinkInt(query.get('gauge_id'));
    if (id != null) {
      withDetailLoader(() =>
        getRequest(`/api/gauges/${id}`).then((res) => {
          const gauge = parseEntityShowResponse(res);
          if (gauge?.id) {
            dispatch({
              type: 'create_new_tab', name: t('Util.Tab.EditGaugeCalibration'), translationKey: 'Util.Tab.EditGaugeCalibration', legacyLabel: t('Util.Tab.EditGaugeCalibration'),
              page: 'GaugeCalibrationAdd',
              props: { gauge, gauge_calibration: gauge, type: 'edit' },
              mask: 'calb005',
            });
          } else {
            showOpenTabWarn(t('Util.DeepLink.Warn.Gauge'));
          }
        }).catch(() => {
          showOpenTabWarn(t('Util.DeepLink.Warn.Gauge'));
        }),
      );
    }
    return;
  }

  if (tab === 'MaintenanceTypeAdd') {
    const id = toDeepLinkInt(query.get('maintenance_type_id'));
    if (id != null) {
      withDetailLoader(() =>
        getRequest(`/api/maintenance_types/${id}`).then((res) => {
          const maintenance_type = parseEntityShowResponse(res);
          if (maintenance_type?.id) {
            dispatch({
              type: 'create_new_tab', name: t('Util.Tab.EditMaintenanceType'), translationKey: 'Util.Tab.EditMaintenanceType', legacyLabel: t('Util.Tab.EditMaintenanceType'),
              page: 'MaintenanceTypeAdd',
              props: { maintenance_type, type: 'edit' },
              mask: 'mntn002',
            });
          } else {
            showOpenTabWarn(t('Util.DeepLink.Warn.MaintenanceType'));
          }
        }).catch(() => {
          showOpenTabWarn(t('Util.DeepLink.Warn.MaintenanceType'));
        }),
      );
    }
    return;
  }

  if (tab === 'MaintenanceTypeList') {
    dispatch({ type: 'create_new_tab', name: t('Util.Tab.MaintenanceTypeList'), translationKey: 'Util.Tab.MaintenanceTypeList', legacyLabel: t('Util.Tab.MaintenanceTypeList'), page: 'MaintenanceTypeList', props: {}, mask: 'mntn001' });
    return;
  }

  if (tab === 'TaskTemplateAdd') {
    const id = toDeepLinkInt(query.get('task_template_id'));
    if (id != null) {
      withDetailLoader(() =>
        getRequest(`/api/task_templates/${id}`).then((res) => {
          const template = parseEntityShowResponse(res);
          if (template?.id) {
            dispatch({
              type: 'create_new_tab', name: t('Util.Tab.EditTaskTemplate'), translationKey: 'Util.Tab.EditTaskTemplate', legacyLabel: t('Util.Tab.EditTaskTemplate'),
              page: 'TaskTemplateAdd',
              props: { template, type: 'edit' },
              mask: 'intr007',
            });
          } else {
            showOpenTabWarn(t('Util.DeepLink.Warn.TaskTemplate'));
          }
        }).catch(() => {
          showOpenTabWarn(t('Util.DeepLink.Warn.TaskTemplate'));
        }),
      );
    }
    return;
  }

  if (tab === 'TaskTemplateList') {
    dispatch({ type: 'create_new_tab', name: t('Util.Tab.TaskTemplateList'), translationKey: 'Util.Tab.TaskTemplateList', legacyLabel: t('Util.Tab.TaskTemplateList'), page: 'TaskTemplateList', props: {}, mask: 'intr008' });
    return;
  }

  if (tab === 'ServiceStateAdd') {
    const id = toDeepLinkInt(query.get('service_state_id'));
    if (id != null) {
      withDetailLoader(() =>
        getRequest(`/api/service_states/${id}`).then((res) => {
          const service_state = parseEntityShowResponse(res);
          if (service_state?.id) {
            dispatch({
              type: 'create_new_tab', name: t('Util.Tab.EditServiceState'), translationKey: 'Util.Tab.EditServiceState', legacyLabel: t('Util.Tab.EditServiceState'),
              page: 'ServiceStateAdd',
              props: { service_state, type: 'edit' },
              mask: 'intr007',
            });
          } else {
            showOpenTabWarn(t('Util.DeepLink.Warn.ServiceState'));
          }
        }).catch(() => {
          showOpenTabWarn(t('Util.DeepLink.Warn.ServiceState'));
        }),
      );
    }
    return;
  }

  if (tab === 'ServiceStateList') {
    dispatch({ type: 'create_new_tab', name: t('Util.Tab.ServiceStateList'), translationKey: 'Util.Tab.ServiceStateList', legacyLabel: t('Util.Tab.ServiceStateList'), page: 'ServiceStateList', props: {}, mask: 'intr006' });
    return;
  }

  if (tab === 'DepartmentList') {
    dispatch({ type: 'create_new_tab', name: t('DepartmentList.FavoriteTitle'), translationKey: 'DepartmentList.FavoriteTitle', legacyLabel: t('DepartmentList.FavoriteTitle'), page: 'DepartmentList', props: {}, mask: 'gset001' });
    return;
  }

  if (tab === 'DepartmentAdd') {
    const id = toDeepLinkInt(query.get('department_id'));
    if (id != null) {
      withDetailLoader(() =>
        getRequest(`/api/departments/${id}`).then((res) => {
          const department = parseEntityShowResponse(res);
          if (department?.id) {
            dispatch({
              type: 'create_new_tab', name: t('DepartmentList.Tab.Edit'), translationKey: 'DepartmentList.Tab.Edit', legacyLabel: t('DepartmentList.Tab.Edit'),
              page: 'DepartmentAdd',
              props: { department, type: 'edit' },
              mask: 'gset002',
            });
          } else {
            showOpenTabWarn();
          }
        }).catch(() => showOpenTabWarn()),
      );
    }
    return;
  }

  if (tab === 'MachineTypeList') {
    dispatch({ type: 'create_new_tab', name: t('MachineTypeList.FavoriteTitle'), translationKey: 'MachineTypeList.FavoriteTitle', legacyLabel: t('MachineTypeList.FavoriteTitle'), page: 'MachineTypeList', props: {}, mask: 'gset015' });
    return;
  }

  if (tab === 'MachineTypeAdd') {
    const id = toDeepLinkInt(query.get('machine_type_id'));
    if (id != null) {
      withDetailLoader(() =>
        getRequest(`/api/machine_types/${id}`).then((res) => {
          const machine_type = parseEntityShowResponse(res);
          if (machine_type?.id) {
            dispatch({
              type: 'create_new_tab', name: t('MachineTypeList.Tab.Edit'), translationKey: 'MachineTypeList.Tab.Edit', legacyLabel: t('MachineTypeList.Tab.Edit'),
              page: 'MachineTypeAdd',
              props: { machine_type, type: 'edit' },
              mask: 'gset016',
            });
          } else {
            showOpenTabWarn();
          }
        }).catch(() => showOpenTabWarn()),
      );
    }
    return;
  }

  if (tab === 'EmployeeList') {
    dispatch({ type: 'create_new_tab', name: t('EmployeeList.FavoriteTitle'), translationKey: 'EmployeeList.FavoriteTitle', legacyLabel: t('EmployeeList.FavoriteTitle'), page: 'EmployeeList', props: {}, mask: 'gset003' });
    return;
  }

  if (tab === 'EmployeeAdd') {
    const id = toDeepLinkInt(query.get('employee_id'));
    if (id != null) {
      withDetailLoader(() =>
        getRequest(`/api/employees/${id}`).then((res) => {
          const employee = parseEntityShowResponse(res);
          if (employee?.id) {
            dispatch({
              type: 'create_new_tab', name: t('EmployeeList.Tab.Edit'), translationKey: 'EmployeeList.Tab.Edit', legacyLabel: t('EmployeeList.Tab.Edit'),
              page: 'EmployeeAdd',
              props: { employee, type: 'edit' },
              mask: 'gset004',
            });
          } else {
            showOpenTabWarn();
          }
        }).catch(() => showOpenTabWarn()),
      );
    }
    return;
  }

  if (tab === 'UserList') {
    dispatch({ type: 'create_new_tab', name: t('UserList'), translationKey: 'UserList', legacyLabel: t('UserList'), page: 'UserList', props: {}, mask: 'gset005' });
    return;
  }

  if (tab === 'UserAdd') {
    const id = toDeepLinkInt(query.get('user_id'));
    if (id != null) {
      withDetailLoader(() =>
        getRequest(`/api/users/${id}`).then((res) => {
          const userRow = parseEntityShowResponse(res);
          if (userRow?.id) {
            dispatch({
              type: 'create_new_tab', name: t('UserList.Tab.Edit'), translationKey: 'UserList.Tab.Edit', legacyLabel: t('UserList.Tab.Edit'),
              page: 'UserAdd',
              props: { user: userRow, type: 'edit', not_last_used: true },
              mask: 'gset006',
            });
          } else {
            showOpenTabWarn();
          }
        }).catch(() => showOpenTabWarn()),
      );
    }
    return;
  }

  if (tab === 'RoleList') {
    dispatch({ type: 'create_new_tab', name: t('RoleList'), translationKey: 'RoleList', legacyLabel: t('RoleList'), page: 'RoleList', props: {}, mask: 'gset011' });
    return;
  }

  if (tab === 'RoleAdd') {
    const id = toDeepLinkInt(query.get('role_id'));
    if (id != null) {
      withDetailLoader(() =>
        getRequest(`/api/roles/${id}`).then((res) => {
          const role = parseEntityShowResponse(res);
          if (role?.id) {
            dispatch({
              type: 'create_new_tab', name: t('RoleList.Tab.Edit'), translationKey: 'RoleList.Tab.Edit', legacyLabel: t('RoleList.Tab.Edit'),
              page: 'RoleAdd',
              props: { role, type: 'edit' },
              mask: 'gset012',
            });
          } else {
            showOpenTabWarn();
          }
        }).catch(() => showOpenTabWarn()),
      );
    }
    return;
  }

  if (tab === 'MachineList') {
    dispatch({ type: 'create_new_tab', name: t('MachineList.FavoriteTitle'), translationKey: 'MachineList.FavoriteTitle', legacyLabel: t('MachineList.FavoriteTitle'), page: 'MachineList', props: {}, mask: 'gset013' });
    return;
  }

  if (tab === 'MachineAdd') {
    const id = toDeepLinkInt(query.get('machine_id'));
    if (id != null) {
      withDetailLoader(() =>
        getRequest(`/api/machines/${id}`).then((res) => {
          const machine = parseEntityShowResponse(res);
          if (machine?.id) {
            dispatch({
              type: 'create_new_tab', name: t('MachineList.Tab.Edit'), translationKey: 'MachineList.Tab.Edit', legacyLabel: t('MachineList.Tab.Edit'),
              page: 'MachineAdd',
              props: { machine, type: 'edit' },
              mask: 'gset014',
            });
          } else {
            showOpenTabWarn(t('Util.DeepLink.Warn.Machine'));
          }
        }).catch(() => showOpenTabWarn(t('Util.DeepLink.Warn.Machine'))),
      );
    }
    return;
  }

  if (tab === 'BusinessPartnerList') {
    dispatch({ type: 'create_new_tab', name: t('BusinessPartnerList.FavoriteTitle'), translationKey: 'BusinessPartnerList.FavoriteTitle', legacyLabel: t('BusinessPartnerList.FavoriteTitle'), page: 'BusinessPartnerList', props: {}, mask: 'gset007' });
    return;
  }

  if (tab === 'BusinessPartnerAdd') {
    const id = toDeepLinkInt(query.get('company_id'));
    if (id != null) {
      withDetailLoader(() =>
        getRequest(`/api/companies/${id}`).then((res) => {
          const business_partner = parseEntityShowResponse(res);
          if (business_partner?.id) {
            dispatch({
              type: 'create_new_tab', name: t('BusinessPartnerList.Tab.Edit'), translationKey: 'BusinessPartnerList.Tab.Edit', legacyLabel: t('BusinessPartnerList.Tab.Edit'),
              page: 'BusinessPartnerAdd',
              props: { business_partner, type: 'edit', not_last_used: true },
              mask: 'gset008',
            });
          } else {
            showOpenTabWarn();
          }
        }).catch(() => showOpenTabWarn()),
      );
    }
    return;
  }

  if (tab === 'ChecklistHolidayList') {
    dispatch({ type: 'create_new_tab', name: t('ChecklistHolidayList.FavoriteTitle'), translationKey: 'ChecklistHolidayList.FavoriteTitle', legacyLabel: t('ChecklistHolidayList.FavoriteTitle'), page: 'ChecklistHolidayList', props: {}, mask: 'gset021' });
    return;
  }

  if (tab === 'ChecklistHolidayAdd') {
    const id = toDeepLinkInt(query.get('checklist_holiday_id'));
    if (id != null) {
      withDetailLoader(() =>
        getRequest(`/api/checklist-holidays/${id}`).then((res) => {
          const holiday = parseEntityShowResponse(res);
          if (holiday?.id) {
            dispatch({
              type: 'create_new_tab', name: t('ChecklistHolidayList.Tab.Edit'), translationKey: 'ChecklistHolidayList.Tab.Edit', legacyLabel: t('ChecklistHolidayList.Tab.Edit'),
              page: 'ChecklistHolidayAdd',
              props: { holiday, type: 'edit', not_last_used: true },
              mask: 'chck014',
            });
          } else {
            showOpenTabWarn();
          }
        }).catch(() => showOpenTabWarn()),
      );
    }
    return;
  }

  if (tab === 'UnitsSettings') {
    dispatch({ type: 'create_new_tab', name: t('UnitsSettings.Title'), translationKey: 'UnitsSettings.Title', legacyLabel: t('UnitsSettings.Title'), page: 'UnitsSettings', props: {}, mask: 'gset017' });
    return;
  }

  if (tab === 'CompanySettings') {
    dispatch({ type: 'create_new_tab', name: t('CompanySettings.Title'), translationKey: 'CompanySettings.Title', legacyLabel: t('CompanySettings.Title'), page: 'CompanySettings', props: {}, mask: 'gset022' });
    return;
  }

  if (tab === 'TrainingRecordDetail') {
    const id = toDeepLinkInt(query.get('employee_id'));
    if (id != null) {
      withDetailLoader(() =>
        getRequest(`/api/employees/${id}`).then((res) => {
          const employee = parseEntityShowResponse(res);
          if (employee?.id) {
            dispatch({
              type: 'create_new_tab', name: t('Util.Tab.EmployeeTraining'), translationKey: 'Util.Tab.EmployeeTraining', legacyLabel: t('Util.Tab.EmployeeTraining'),
              page: 'TrainingRecordDetail',
              props: { employee, type: 'edit' },
              mask: 'trng013',
            });
          } else {
            showOpenTabWarn();
          }
        }).catch(() => showOpenTabWarn()),
      );
    }
    return;
  }

  if (tab === 'SupportTicketList' || tab === 'SupportTicketDetail') {
    const query_ticket_id = query.get('ticket_id');
    if (query_ticket_id) {
      dispatch({
        type: 'create_new_tab', name: t('Util.Tab.TicketDetail'), translationKey: 'Util.Tab.TicketDetail', legacyLabel: t('Util.Tab.TicketDetail'),
        page: 'SupportTicketDetail',
        props: { ticketId: query_ticket_id },
        mask: 'help002',
      });
    }
    return;
  }

  if (tab === 'ServiceTaskDetail') {
    const query_task_id = query.get('task_id');
    if (query_task_id) {
      dispatch({
        type: 'create_new_tab', name: t('Util.Tab.ServiceTaskDetail'), translationKey: 'Util.Tab.ServiceTaskDetail', legacyLabel: t('Util.Tab.ServiceTaskDetail'),
        page: 'ServiceTaskDetail',
        props: { taskId: query_task_id },
        mask: 'intr003',
      });
    }
    return;
  }

  if (tab === 'CrmCommunicationTicketDetail') {
    const crmTicketId = toDeepLinkInt(query.get('ticket_id'));
    if (crmTicketId != null) {
      const portalView = resolveCrmTicketDetailPortalView(user);
      dispatch({
        type: 'create_new_tab', name: t('CrmCommunication.Tab.TicketDetail'), translationKey: 'CrmCommunication.Tab.TicketDetail', legacyLabel: t('CrmCommunication.Tab.TicketDetail'),
        page: 'CrmCommunicationTicketDetail',
        props: { ticketId: crmTicketId, portalView },
        mask: portalView ? CRM_COMM_MASK_PORTAL_DETAIL : CRM_COMM_MASK_DETAIL,
      });
    }
    return;
  }

  const openCrmCustomerDetail = (page, titleKey, mask) => {
    const customerId = toDeepLinkInt(query.get('customerId'));
    if (customerId == null) return;
    withDetailLoader(() =>
      getRequest(`/api/customer_evaluations/my_customers/${customerId}`).then((res) => {
        const row = res?.data ?? res;
        if (row?.id) {
          dispatch({
            type: 'create_new_tab',
            name: row.name || t(titleKey), translationKey: titleKey, legacyLabel: row.name || t(titleKey),
            page,
            props: { customerId: row.id, customerRow: row, type: 'edit' },
            mask,
          });
        } else {
          showOpenTabWarn(t('Util.DeepLink.Warn.CrmCustomer'));
        }
      }).catch(() => {
        showOpenTabWarn(t('Util.DeepLink.Warn.CrmCustomer'));
      }),
    );
  };

  if (tab === 'CustomerMyCustomersDetail') {
    openCrmCustomerDetail('CustomerMyCustomersDetail', 'CustomerMyCustomersDetail.Title', 'cust014');
    return;
  }
  if (tab === 'CustomerAllPartnersDetail') {
    openCrmCustomerDetail('CustomerAllPartnersDetail', 'CustomerAllPartnersDetail.Title', 'cust017');
    return;
  }
  if (tab === 'CustomerMyBusinessByCustomerDetail') {
    openCrmCustomerDetail('CustomerMyBusinessByCustomerDetail', 'CustomerMyBusinessByCustomerDetail.Title', 'cust026');
    return;
  }

  if (tab === 'OtkCompanyCertRecordDetail') {
    openCompanyCertDeepLink(ctx, {
      page: 'OtkCompanyCertRecordDetail',
      propKey: 'otk_company',
      mask: 'qlty029',
      titleKey: 'Util.Tab.OtkPartnerDocuments',
      companyId: query.get('company_id'),
    });
    return;
  }
  if (tab === 'CalibrationCompanyCertRecordDetail') {
    openCompanyCertDeepLink(ctx, {
      page: 'CalibrationCompanyCertRecordDetail',
      propKey: 'calibration_company',
      mask: 'calb011',
      titleKey: 'Util.Tab.CalibrationCompanyCertValidity',
      companyId: query.get('company_id'),
    });
    return;
  }
  if (tab === 'TrainingCompanyCertRecordDetail') {
    openCompanyCertDeepLink(ctx, {
      page: 'TrainingCompanyCertRecordDetail',
      propKey: 'training_company',
      mask: 'trng025',
      titleKey: 'Util.Tab.TrainingCompanyCertValidity',
      companyId: query.get('company_id'),
    });
    return;
  }
  if (tab === 'MaintenanceCompanyCertRecordDetail') {
    openCompanyCertDeepLink(ctx, {
      page: 'MaintenanceCompanyCertRecordDetail',
      propKey: 'maintenance_company',
      mask: 'mntn009',
      titleKey: 'Util.Tab.MaintenanceCompanyCertValidity',
      companyId: query.get('company_id'),
    });
    return;
  }

  if (tab === 'ChecklistRecordAdd') {
    if (!canCreateChecklistRecord(user)) {
      showOpenTabWarn(t('ChecklistRecord.Create.PermissionDenied'));
      return;
    }
    const id = toDeepLinkInt(query.get('template_id'));
    if (id != null) {
      withDetailLoader(() =>
        getRequest(`/api/checklist_templates/${id}`).then((res) => {
          const tpl = parseEntityShowResponse(res);
          if (tpl?.id) {
            dispatch({
              type: 'create_new_tab', name: t('Util.Tab.EditChecklistRecord'), translationKey: 'Util.Tab.EditChecklistRecord', legacyLabel: t('Util.Tab.EditChecklistRecord'),
              page: 'ChecklistRecordAdd',
              props: { checklist_template: tpl },
              mask: 'chck004',
            });
          } else {
            showOpenTabWarn(t('Util.DeepLink.Warn.ChecklistTemplate'));
          }
        }).catch(() => {
          showOpenTabWarn(t('Util.DeepLink.Warn.ChecklistTemplate'));
        }),
      );
    }
    return;
  }

  if (tab === 'ChecklistStateDetail') {
    const queryRecordId = query.get('record_id');
    if (queryRecordId) {
      const isManager = user?.permissions?.includes('edit checklists');
      dispatch({
        type: 'create_new_tab', name: t('Util.Tab.ChecklistRecord'), translationKey: 'Util.Tab.ChecklistRecord', legacyLabel: t('Util.Tab.ChecklistRecord'),
        page: isManager ? 'ChecklistStateDetail' : 'ChecklistRecordMyDetail',
        props: { recordId: queryRecordId },
        mask: 'chck006',
      });
    }
    return;
  }

  if (tab === 'MaintenanceRecordDetail') {
    const id = toDeepLinkInt(query.get('machine_id'));
    if (id != null) {
      withDetailLoader(() =>
        getRequest(`/api/machines/${id}`).then((res) => {
          const machine = parseEntityShowResponse(res);
          if (machine?.id) {
            dispatch({
              type: 'create_new_tab', name: t('Util.Tab.MaintenancePlannedTasks'), translationKey: 'Util.Tab.MaintenancePlannedTasks', legacyLabel: t('Util.Tab.MaintenancePlannedTasks'),
              page: 'MaintenanceRecordDetail',
              props: { machine, type: 'edit' },
              mask: 'mntn020',
            });
          } else {
            showOpenTabWarn(t('Util.DeepLink.Warn.Machine'));
          }
        }).catch(() => {
          showOpenTabWarn(t('Util.DeepLink.Warn.Machine'));
        }),
      );
    }
    return;
  }

  if (tab === 'MaintenanceMachineStateList') {
    const id = toDeepLinkInt(query.get('machine_id'));
    if (id != null) {
      withDetailLoader(() =>
        getRequest(`/api/machines/${id}`).then((res) => {
          const machine = parseEntityShowResponse(res);
          if (machine?.id) {
            dispatch({
              type: 'create_new_tab', name: t('Util.Tab.MachineState'), translationKey: 'Util.Tab.MachineState', legacyLabel: t('Util.Tab.MachineState'),
              page: 'MaintenanceMachineStateDetail',
              props: { machine },
              mask: 'mntn038',
            });
          } else {
            dispatch({
              type: 'create_new_tab', name: t('Util.Tab.MachineStatesList'), translationKey: 'Util.Tab.MachineStatesList', legacyLabel: t('Util.Tab.MachineStatesList'),
              page: 'MaintenanceMachineStateList',
              props: {},
              mask: 'mntn037',
            });
          }
        }).catch(() => {
          dispatch({
            type: 'create_new_tab', name: t('Util.Tab.MachineStatesList'), translationKey: 'Util.Tab.MachineStatesList', legacyLabel: t('Util.Tab.MachineStatesList'),
            page: 'MaintenanceMachineStateList',
            props: {},
            mask: 'mntn037',
          });
        }),
      );
    } else {
      dispatch({
        type: 'create_new_tab', name: t('Util.Tab.MachineStatesList'), translationKey: 'Util.Tab.MachineStatesList', legacyLabel: t('Util.Tab.MachineStatesList'),
        page: 'MaintenanceMachineStateList',
        props: {},
        mask: 'mntn037',
      });
    }
    return;
  }

  if (tab === 'ChecklistStateList') {
    dispatch({
      type: 'create_new_tab', name: t('Util.Tab.ChecklistRecordsList'), translationKey: 'Util.Tab.ChecklistRecordsList', legacyLabel: t('Util.Tab.ChecklistRecordsList'),
      page: 'ChecklistStateList',
      props: {},
      mask: 'chck005',
    });
  }
}
